import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputField } from '../UI';
import { Toggle } from '../UI/base';
import type { ClosedDayBookingRequest, ClosedDayBookingResponse } from '../../api/generated';
import { BookingFormModalShell, isSingleOccurrenceTime } from '../Bookings/shared';
import { ClosedDayFields } from './ClosedDayFields';
import {
  CLOSED_DAY_BOOKING_TYPE,
  DEFAULT_PARTIAL_END_TIME
} from './closedDayBookingConstants';
import {
  buildFullDayOccurrence,
  buildPartialDayOccurrence,
  splitClosedDayOccurrence,
  type ClosedDayFormState
} from './closedDayTime';

const KEY_PREFIX = 'closed-day';

interface ClosedDayBookingFormModalProps {
  booking: ClosedDayBookingResponse | null;
  onSave: (payload: ClosedDayBookingRequest) => void;
  onClose: () => void;
}

interface ClosedDayFormErrors {
  date?: string;
  toDate?: string;
  time?: string;
}

// A new closure defaults to a single full day; an existing one loads its stored shape. A Closed day
// is always single, so a recurring time (which this UI never creates) just falls back to the default.
const buildInitialState = (booking: ClosedDayBookingResponse | null): ClosedDayFormState => {
  if (booking && isSingleOccurrenceTime(booking.time)) {
    return splitClosedDayOccurrence(booking.time);
  }
  return { isFullDay: true, fromDate: '', toDate: '', startTime: '', endTime: '' };
};

export const ClosedDayBookingFormModal = ({
  booking,
  onSave,
  onClose
}: ClosedDayBookingFormModalProps) => {
  const { t } = useTranslation();
  const initial = buildInitialState(booking);
  const [isFullDay, setIsFullDay] = useState(initial.isFullDay);
  const [fromDate, setFromDate] = useState(initial.fromDate);
  const [toDate, setToDate] = useState(initial.toDate);
  const [startTime, setStartTime] = useState(initial.startTime);
  const [endTime, setEndTime] = useState(initial.endTime);
  const [reason, setReason] = useState(booking?.reason ?? '');
  const [errors, setErrors] = useState<ClosedDayFormErrors>({});

  // The To date rides along with From: empty or now-too-early values snap up, so the common
  // single-day closure needs only the From date while a range stays a deliberate edit.
  const handleFromDateChange = (date: string) => {
    setFromDate(date);
    if (isFullDay && (!toDate || toDate < date)) setToDate(date);
  };

  const handleToggleFullDay = () => {
    const next = !isFullDay;
    if (next && (!toDate || toDate < fromDate)) setToDate(fromDate);
    if (!next && !endTime) setEndTime(DEFAULT_PARTIAL_END_TIME);
    setIsFullDay(next);
  };

  const validate = (): boolean => {
    const next: ClosedDayFormErrors = {};
    if (!fromDate) next.date = t('closed-day-error-date');
    if (isFullDay) {
      if (toDate && toDate < fromDate) next.toDate = t('closed-day-error-to-date');
    } else if (!startTime || !endTime) {
      next.time = t('closed-day-error-time-required');
    } else if (endTime <= startTime) {
      next.time = t('closed-day-error-time-range');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const time = isFullDay
      ? buildFullDayOccurrence(fromDate, toDate)
      : buildPartialDayOccurrence(fromDate, startTime, endTime);
    onSave({ type: CLOSED_DAY_BOOKING_TYPE, reason: reason.trim() || undefined, time });
  };

  return (
    <BookingFormModalShell
      keyPrefix={KEY_PREFIX}
      isEditing={booking !== null}
      onSave={handleSave}
      onClose={onClose}
    >
      <Toggle
        label={t('closed-day-full-day-label')}
        checked={isFullDay}
        onChange={handleToggleFullDay}
      />

      <ClosedDayFields
        isFullDay={isFullDay}
        fromDate={fromDate}
        toDate={toDate}
        startTime={startTime}
        endTime={endTime}
        dateError={errors.date}
        toDateError={errors.toDate}
        timeError={errors.time}
        onFromDateChange={handleFromDateChange}
        onToDateChange={setToDate}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
      />

      <InputField
        label={t('closed-day-reason-label')}
        type="text"
        value={reason}
        placeholder={t('closed-day-reason-placeholder')}
        onChange={(e) => setReason(e.target.value)}
      />
    </BookingFormModalShell>
  );
};
